import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { buttonVariants } from '@/components/ui/button'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'

import { mainNav } from '@/constants/site'

const isActive = (pathname: string, href: string): boolean => {
    if (href.includes('#')) return false
    if (href === '/') return pathname === '/'

    return pathname === href || pathname.startsWith(`${href}/`)
}

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    return (
        <header
            className={cn(
                'sticky top-0 z-40 w-full border-b transition-[background-color,border-color,box-shadow] duration-300',
                scrolled
                    ? 'border-border/80 bg-background/85 shadow-[0_1px_12px_-6px_rgb(0_0_0/0.15)] backdrop-blur-md'
                    : 'border-transparent bg-background'
            )}
        >
            <div className='mx-auto flex h-18 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <Logo />

                <nav aria-label='Main' className='hidden items-center gap-1 md:flex'>
                    {mainNav.map(item => (
                        <Link
                            key={item.href}
                            href={item.href}
                            aria-current={isActive(router.pathname, item.href) ? 'page' : undefined}
                            className={cn(
                                'rounded-full px-4 py-2 text-sm font-medium transition-colors outline-none hover:bg-secondary hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/40',
                                isActive(router.pathname, item.href) ? 'bg-secondary text-foreground' : 'text-muted-foreground'
                            )}
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href='/contact'
                        className={cn(buttonVariants({ size: 'default' }), 'hidden sm:inline-flex')}
                    >
                        Get a quote
                    </Link>

                    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                        <SheetTrigger
                            className={cn(buttonVariants({ variant: 'outline', size: 'icon' }), 'md:hidden')}
                            aria-label='Open menu'
                        >
                            <Menu className='size-5' />
                        </SheetTrigger>

                        <SheetContent side='right' className='gap-0 p-0'>
                            <div className='border-b border-border p-5 pr-16'>
                                <SheetTitle className='font-heading text-xl'>
                                    Menu
                                </SheetTitle>

                                <SheetDescription>
                                    Handcrafted tiny houses on wheels.
                                </SheetDescription>
                            </div>

                            <nav aria-label='Mobile' className='flex flex-col p-3'>
                                <Link
                                    href='/'
                                    className='rounded-xl px-4 py-3.5 text-lg font-medium transition-colors hover:bg-secondary'
                                    onClick={() => setMenuOpen(false)}
                                >
                                    Home
                                </Link>

                                {mainNav.map(item => (
                                    <Link
                                        key={item.href}
                                        href={item.href}
                                        aria-current={isActive(router.pathname, item.href) ? 'page' : undefined}
                                        className={cn(
                                            'rounded-xl px-4 py-3.5 text-lg font-medium transition-colors hover:bg-secondary',
                                            isActive(router.pathname, item.href) && 'bg-secondary'
                                        )}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </nav>

                            <div className='mt-auto border-t border-border p-5'>
                                <Link
                                    href='/contact'
                                    className={cn(buttonVariants({ size: 'lg' }), 'w-full')}
                                    onClick={() => setMenuOpen(false)}
                                >
                                    Get a quote
                                    <ArrowRight className='size-4' />
                                </Link>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    )
}
